import { ReactElement } from 'react';
import { formatCount } from '@sc/shared-ui';
import classNames from 'classnames';
import { UseUsageType } from '../hooks/useUsage';

/**
 * The queries + page-imports meters: used count against the plan allowance
 * and a thin progress bar (query bar coloured by depletion state). Shared by
 * the Topbar usage dropdown and the billing Monthly usage panel.
 */
const usedPercent = (pool: { total: number; used: number }): number =>
  pool.total > 0
    ? Math.min(100, Math.round((pool.used / pool.total) * 100))
    : 0;

export const UsageBars = ({
  usage,
  usageState,
}: {
  usage: UseUsageType['usage'];
  usageState: UseUsageType['usageState'];
}): ReactElement => (
  <>
    <div className="flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-body-medium font-semibold">Queries</span>
        <span className="text-body-medium font-semibold">
          {formatCount(usage.query.used)} / {formatCount(usage.query.total)}
        </span>
      </div>
      <div
        className="w-full bg-grey-t-800 relative h-1 rounded-xs overflow-hidden"
        role="progressbar"
        aria-valuenow={usage.query.used}
        aria-valuemin={0}
        aria-valuemax={usage.query.total}
      >
        <div
          className={classNames(
            'h-full transition-all rounded-xs duration-300 ease-out',
            {
              'bg-primary-400': usageState === 'healthy',
              'bg-yellow-400': usageState === 'notice',
              'bg-red-400': usageState === 'warning',
            }
          )}
          style={{
            width: `${usedPercent(usage.query)}%`,
          }}
        />
      </div>
    </div>

    <div className="flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-body-medium font-semibold">Page imports</span>
        <span className="text-body-medium font-semibold">
          {formatCount(usage.ingestion.used)} /{' '}
          {formatCount(usage.ingestion.total)}
        </span>
      </div>
      <div
        className="w-full bg-grey-t-800 relative h-1 rounded-xs overflow-hidden"
        role="progressbar"
        aria-valuenow={usage.ingestion.used}
        aria-valuemin={0}
        aria-valuemax={usage.ingestion.total}
      >
        <div
          className="h-full bg-primary-400 transition-all rounded-xs duration-300 ease-out"
          style={{
            width: `${usedPercent(usage.ingestion)}%`,
          }}
        />
      </div>
    </div>
  </>
);
